import { FormEvent, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ServerErrors } from 'dash-common';
import {
  Form,
  FormContext,
  FormErrorTypes,
  Modal,
  ModalContext,
  Toggle,
} from 'dash-ui';

import {
  GetOrganizationUsersForTableDocument,
  useCreateOrganizationUserMutation,
  useEditOrganizationUserMutation,
} from 'gql/generated.graphql';
import { OrganizationUsersModalsEnum } from 'pages/organizations/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/constants/constants';
import { FormFields } from './constants/constants';
import useValidation from './hooks/useValidation';
import GeneralInputs from './components/GeneralInputs/GeneralInputs';
import RoleSelect from './components/RoleSelect/RoleSelect';
import PropertiesSelect from './components/PropertiesSelect/PropertiesSelect';
import AccountStatusSelect from './components/AccountStatusSelect/AccountStatusSelect';

function SaveOrganizationUsersModal({
  organizationId,
}: {
  organizationId: string;
}) {
  const {
    isOpenCurrent,
    toggleModal,
    setPayload,
    payload: user,
  } = useContext(ModalContext);
  const isOpen =
    isOpenCurrent[OrganizationUsersModalsEnum.SAVE_ORGANIZATION_USER] || false;
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const {
    formState,
    formOnSubmit,
    formOnError,
    resetForm,
    resetFormErrors,
    stateHandlers,
    errorHandlers,
    setFormValues,
  } = useContext(FormContext);
  const valid = useValidation();
  const isEditMode = Boolean(user);

  const [createOrganizationUserMutation, { loading: createLoading }] =
    useCreateOrganizationUserMutation({
      refetchQueries: [GetOrganizationUsersForTableDocument],
    });

  const [editOrganizationUserMutation, { loading: editLoading }] =
    useEditOrganizationUserMutation({
      refetchQueries: [GetOrganizationUsersForTableDocument],
    });

  useEffect(() => {
    if (user) {
      setFormValues({
        firstName: user?.profile?.firstName,
        lastName: user?.profile?.lastName,
        email: user?.account?.email,
        excludeFromReports: user?.account?.excludeFromReports,
        role: user?.saleTeamRole || user?.account?.role,
        properties: user?.assignedPropertiesIds?.map((_id: string) => ({
          _id,
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const closeModal = () => {
    resetForm();
    resetFormErrors();
    toggleModal(false, OrganizationUsersModalsEnum.SAVE_ORGANIZATION_USER);
    setPayload(undefined);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;
    const { firstName, lastName, email, excludeFromReports, role, properties } =
      formValues;

    if (
      !valid(FormFields.FirstName, firstName) ||
      !valid(FormFields.LastName, lastName) ||
      !valid(FormFields.Email, email) ||
      !valid(FormFields.Role, role?.type) ||
      !valid(FormFields.Properties, properties)
    ) {
      return null;
    }

    const propertyIds =
      properties?.map(({ _id }: { _id: string }) => _id) || [];
    const basicInput = {
      accountInput: {
        email,
        excludeFromReports,
      },
      roleId: role._id,
      profileInput: { firstName, lastName },
      ...(propertyIds.length ? { assignedPropertiesIds: propertyIds } : {}),
    };

    const onError = (error: { message: string }) => {
      if (
        [
          ServerErrors.ACCOUNT_EMAIL_ALREADY_EXIST as string,
          ServerErrors.ACCOUNT_ARCHIVED as string,
        ].includes(error?.message)
      ) {
        return errorHandlers[FormFields.Email]({
          type: FormErrorTypes.Invalid,
          message: t(`common:serverErrors:${error.message}`),
        });
      }
      return formOnError(
        t(`common:serverMessages.${error.message}`) || error.message
      );
    };

    if (isEditMode) {
      return editOrganizationUserMutation({
        variables: { input: { ...basicInput, organizationUserId: user._id } },
        onCompleted: closeModal,
        onError: (error: { message: string }) => {
          return formOnError(
            t(`common:serverMessages.${error.message}`) || error.message
          );
        },
      });
    }

    return createOrganizationUserMutation({
      variables: { input: { ...basicInput, organizationId } },
      onCompleted: (data) => {
        closeModal();
        toggleModal(
          true,
          OrganizationUsersModalsEnum.SEND_REGISTRATION_LINK_TO_USER
        );
        setPayload(data.createOrganizationUser.account._id);
      },
      onError,
    });
  };

  const isLoading = createLoading || editLoading;

  return (
    <Modal
      title={
        !isEditMode
          ? t('pages:organizationUsers.addOrganisationUserModalLabel') || ''
          : t('pages:organizationUsers.editOrganisationUserModalLabel') || ''
      }
      isOpen={isOpen}
      onRequestClose={closeModal}
      submitButton={{
        title: isEditMode ? t('common:labels.update') : t('common:labels.add'),
        isLoading: isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <GeneralInputs orgUser={user} />
        <RoleSelect />
        {isEditMode && <AccountStatusSelect status={user?.account?.status} />}
        <PropertiesSelect organizationId={organizationId} />
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.excludeFromReports')}</Form.Label>
          <Toggle
            // @ts-ignore
            checked={formState.excludeFromReports}
            flexProps={{ mx: 2 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { checked } = e.target;
              stateHandlers[FormFields.ExcludeFromReports](checked);
            }}
          />
        </Form.Group>
        <Form.Group mt={3}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
}

export default SaveOrganizationUsersModal;
